/**
 * CorpusPulseBanner — surfaces the project's corpus fight on the Home page
 * for verified members. Renders nothing when no case/docs/events exist or
 * the user is not a verified member of a project.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Gavel, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CorpusPulseBanner({ user, onNavigate }) {
  const [pulse, setPulse] = useState(null);
  const projectId = user?.project_id || user?.verified_projects?.[0];

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/corpus-warroom/${projectId}/pulse`);
        if (!cancelled && res.data?.active) setPulse(res.data);
      } catch { /* not a member or no case — stay hidden */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (!pulse) return null;

  return (
    <div
      className="container mx-auto px-4 mt-4"
      data-testid="corpus-pulse-banner"
    >
      <div className="max-w-4xl mx-auto rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 via-background to-background p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="p-2 rounded-full bg-emerald-100 shrink-0 self-start">
          <Gavel className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold">{pulse.title || 'Corpus Protection'}</p>
            <Badge className="bg-emerald-600 text-white text-[10px] animate-pulse">LIVE</Badge>
            {pulse.stage && <Badge variant="outline" className="text-[10px]">{pulse.stage}</Badge>}
          </div>
          {pulse.latest_event && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              Latest: {pulse.latest_event}
              {pulse.latest_event_date && ` · ${new Date(pulse.latest_event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
            </p>
          )}
        </div>
        <Button size="sm" onClick={() => onNavigate?.('corpus-warroom')} data-testid="corpus-pulse-open" className="shrink-0">
          Open Protection Room <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
